import React from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '../common/Button';
import Dropdown from '../common/Dropdown';
import FormField from '../common/FormField';
import InputField from '../common/InputField';
import Modal from '../common/Modal';

interface EditLubricantProductModalProps {
  onClose: () => void;
  onSubmit: () => void; // should be wrapped with react-hook-form handleSubmit in parent
  control: Control<{
    productName: string;
    maker: string;
    category: string;
    type: string;
    group: string;
    unit: string;
    status: string;
  }>;
  errors?: FieldErrors<{
    productName: string;
    maker: string;
    category: string;
    type: string;
    group: string;
    unit: string;
    status: string;
  }>;
  isEditMode?: boolean;
  makersOptions: Array<{ value: string; label: string }>;
  categoriesOptions: Array<{ value: string; label: string }>;
  typesOptions: Array<{ value: string; label: string }>;
  groupsOptions: Array<{ value: string; label: string }>;
  unitsOptions: Array<{ value: string; label: string }>;
  isSubmitting?: boolean;
}

const EditLubricantProductModal: React.FC<EditLubricantProductModalProps> = ({
  onClose,
  onSubmit,
  control,
  errors,
  isEditMode = false,
  makersOptions = [],
  categoriesOptions = [],
  typesOptions = [],
  groupsOptions = [],
  unitsOptions = [],
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  return (
    <Modal
      size="sm"
      open={true}
      onClose={onClose}
      title={isEditMode ? t('lubricants.editProduct') : t('lubricants.addProduct')}
      footer={
        <>
          <Button variant="red" sx={{ fontSize: '14px' }} onClick={onClose}>
            {t('buttons.cancel')}
          </Button>

          <Button
            variant="outlinedBlue"
            sx={{ fontSize: '14px' }}
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t('buttons.adding')
              : isEditMode
                ? t('buttons.update')
                : t('buttons.add')}
          </Button>
        </>
      }
    >
      <Box sx={{ mt: 2 }}>
        <FormField label={t('form.productName')} required>
          <Controller
            name="productName"
            control={control}
            rules={{ required: t('form.enterProductName') }}
            render={({ field }) => (
              <InputField
                {...field}
                value={field.value || ''}
                sx={{ flex: 1 }}
                placeholder={t('form.enterProductName')}
                error={!!errors?.productName}
                helperText={errors?.productName?.message as string}
              />
            )}
          />
        </FormField>
        <FormField label={t('form.maker')} required>
          <Controller
            name="maker"
            control={control}
            rules={{ required: t('form.selectMaker') }}
            render={({ field }) => (
              <Dropdown
                value={field.value ?? ''}
                onChange={(e: any) => field.onChange(e.target.value)}
                options={makersOptions}
                sx={{ flex: 1 }}
                placeholder={t('form.selectMaker')}
                debounceMs={200}
                maxDisplayedOptions={50}
                error={!!errors?.maker}
                helperText={errors?.maker?.message as string}
              />
            )}
          />
        </FormField>

        <FormField label={t('form.category')} required>
          <Controller
            name="category"
            control={control}
            rules={{ required: t('form.selectCategory') }}
            render={({ field }) => (
              <Dropdown
                value={field.value ?? ''}
                onChange={(e: any) => field.onChange(e.target.value)}
                options={categoriesOptions}
                sx={{ flex: 1 }}
                placeholder={t('form.selectCategory')}
                debounceMs={200}
                maxDisplayedOptions={50}
                error={!!errors?.category}
                helperText={errors?.category?.message as string}
              />
            )}
          />
        </FormField>
        <FormField label={t('form.type')} required>
          <Controller
            name="type"
            control={control}
            rules={{ required: t('form.selectType') }}
            render={({ field }) => (
              <Dropdown
                value={field.value ?? ''}
                onChange={(e: any) => field.onChange(e.target.value)}
                options={typesOptions}
                sx={{ flex: 1 }}
                placeholder={t('form.selectType')}
                debounceMs={200}
                maxDisplayedOptions={50}
                error={!!errors?.type}
                helperText={errors?.type?.message as string}
              />
            )}
          />
        </FormField>

        <FormField label={t('form.group')} required>
          <Controller
            name="group"
            control={control}
            rules={{ required: t('form.selectGroup') }}
            render={({ field }) => (
              <Dropdown
                value={field.value ?? ''}
                onChange={(e: any) => field.onChange(e.target.value)}
                options={groupsOptions}
                sx={{ flex: 1 }}
                placeholder={t('form.selectGroup')}
                debounceMs={200}
                maxDisplayedOptions={50}
                error={!!errors?.group}
                helperText={errors?.group?.message as string}
              />
            )}
          />
        </FormField>

        <FormField label={t('form.unit')} required>
          <Controller
            name="unit"
            control={control}
            rules={{ required: t('form.selectUnit') }}
            render={({ field }) => (
              <Dropdown
                value={field.value ?? ''}
                onChange={(e: any) => field.onChange(e.target.value)}
                options={unitsOptions}
                sx={{ flex: 1 }}
                placeholder={t('form.selectUnit')}
                debounceMs={200}
                maxDisplayedOptions={50}
                error={!!errors?.unit}
                helperText={errors?.unit?.message as string}
              />
            )}
          />
        </FormField>

        <FormField label={t('form.status')} required>
          <Controller
            name="status"
            control={control}
            rules={{ required: t('common.selectStatus') }}
            render={({ field }) => (
              <Dropdown
                value={field.value ?? 'Active'}
                onChange={(e: any) => field.onChange(e.target.value)}
                options={[
                  { value: 'Active', label: t('common.active') },
                  { value: 'Inactive', label: t('common.inactive') },
                ]}
                sx={{ flex: 1 }}
                placeholder={t('common.selectStatus')}
                hasSearch={false}
                error={!!errors?.status}
                helperText={errors?.status?.message as string}
              />
            )}
          />
        </FormField>
      </Box>
    </Modal>
  );
};

export default EditLubricantProductModal;
